import {
  BaseException,
  ListOutput,
  LoginOutput,
  RefreshInput,
  RefreshOutput,
  StandardResponse,
} from '@allocab/allocab-commons-shared-types';
import { captureException } from '@sentry/browser';
import axios from 'axios';
import type { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios';
import dayjs from 'dayjs';

import { APIError, APIResponseDataType } from './types';
import { constants } from '../../constants';
import { clearTokens, updateTokens } from '../../utils/auth';
import { queryKeys } from '../datastore/queryKeys';
import { queryClient } from '../datastore/react-query';
import { locale, t } from '../translation/translation';

type Options = Omit<AxiosRequestConfig, 'params'> & {
  skipAuth?: boolean;
};

class API {
  axiosInstance: AxiosInstance;

  constructor(baseURL: string) {
    this.axiosInstance = axios.create({
      baseURL,
      headers: { 'Accept-Language': locale },
      timeout: 60 * 1000,
    });
    this.axiosInstance.interceptors.response.use(
      response => {
        if (response?.headers?.['content-type'] === 'application/pdf') {
          return response?.data;
        }

        return response?.data?.data;
      },
      (error: AxiosError) => {
        if (error.response) {
          // The request was made and the server responded with an expected error (functional or auth for example)
          let message = t('network.server-error');
          let errorMessage = undefined;
          const config = {
            baseURL: error.response.config.baseURL,
            url: error.response.config.url,
            params: error.response.config.params,
          };

          if (error.response.headers['content-type']?.includes('application/json')) {
            const result = error.response.data as StandardResponse<BaseException>;

            errorMessage = result.data.errorMessage;
            if (result.message) message = result.message;

            if ((errorMessage === 'INVALID_TOKEN' || errorMessage === 'INVALID_USER_TYPE') && import.meta.env.PROD) {
              clearTokens(queryClient);
            }

            if (error.response.status === 400) {
              captureException(errorMessage, {
                tags: { API_ERROR_TYPE: 'server' },
                extra: { config, userMessage: message },
              });
            }
          } else {
            captureException(`Expected JSON response from the API, got: ${error.response.headers['content-type']}`, {
              tags: { API_ERROR_TYPE: 'server' },
              extra: { config },
            });
          }

          throw { type: 'api', message, errorMessage } satisfies APIError;
        }

        if (error.request) {
          // The request was made but no response was received (no network for example)
          throw { type: 'api', message: t('network.network-error') } satisfies APIError;
        }

        // Something happened in setting up the request that triggered an Error
        captureException(error, { tags: { API_ERROR_TYPE: 'code' } });

        throw { type: 'api', message: t('network.code-error') } satisfies APIError;
      },
    );
  }

  get = async <Input, Output, Format extends APIResponseDataType = 'item'>(
    path: string,
    params?: Input,
    { skipAuth, ...axiosConfig }: Options = {},
  ) => {
    const tokens = skipAuth ? null : await getFreshTokens();

    return this.axiosInstance.get<undefined, Format extends 'list' ? ListOutput<Output> : Output>(path, {
      ...axiosConfig,
      params,
      headers: {
        'Accept-Language': locale,
        ...(tokens ? { Authorization: `Bearer ${tokens.accessToken}` } : {}),
        ...axiosConfig.headers,
      },
    });
  };

  post = async <Input, Output, Format extends APIResponseDataType = 'item'>(
    path: string,
    data?: Input,
    { skipAuth, ...axiosConfig }: Options = {},
  ) => {
    const tokens = skipAuth ? null : await getFreshTokens();

    let filtereData = data;

    // TODO: Remove dirty trick waiting for an API fix
    if (path === '/authentication/v1/user/me') {
      filtereData = { accessToken: tokens?.accessToken } as unknown as Input;
    }

    return this.axiosInstance.post<undefined, Format extends 'list' ? ListOutput<Output> : Output>(path, filtereData, {
      ...axiosConfig,
      headers: { ...(tokens ? { Authorization: `Bearer ${tokens.accessToken}` } : {}), ...axiosConfig.headers },
    });
  };

  postFormData = async <Output, Format extends APIResponseDataType = 'item'>(
    path: string,
    data: FormData,
    { skipAuth, ...axiosConfig }: Options = {},
  ) => {
    const tokens = skipAuth ? null : await getFreshTokens();

    return this.axiosInstance.post<undefined, Format extends 'list' ? ListOutput<Output> : Output>(path, data, {
      ...axiosConfig,
      headers: {
        ...(tokens ? { Authorization: `Bearer ${tokens.accessToken}` } : {}),
        'Content-Type': 'multipart/form-data',
        ...axiosConfig.headers,
      },
    });
  };

  put = async <Input, Output, Format extends APIResponseDataType = 'item'>(
    path: string,
    data: Input,
    { skipAuth, ...axiosConfig }: Options = {},
  ) => {
    const tokens = skipAuth ? null : await getFreshTokens();

    return this.axiosInstance.put<undefined, Format extends 'list' ? ListOutput<Output> : Output>(path, data, {
      ...axiosConfig,
      headers: { ...(tokens ? { Authorization: `Bearer ${tokens.accessToken}` } : {}), ...axiosConfig.headers },
    });
  };

  patch = async <Input, Output, Format extends APIResponseDataType = 'item'>(
    path: string,
    data?: Input,
    { skipAuth, ...axiosConfig }: Options = {},
  ) => {
    const tokens = skipAuth ? null : await getFreshTokens();

    return this.axiosInstance.patch<undefined, Format extends 'list' ? ListOutput<Output> : Output>(path, data, {
      ...axiosConfig,
      headers: { ...(tokens ? { Authorization: `Bearer ${tokens.accessToken}` } : {}), ...axiosConfig.headers },
    });
  };

  delete = async <Output, Format extends APIResponseDataType = 'item'>(
    path: string,
    { skipAuth, ...axiosConfig }: Options = {},
  ) => {
    const tokens = skipAuth ? null : await getFreshTokens();

    return this.axiosInstance.delete<undefined, Format extends 'list' ? ListOutput<Output> : Output>(path, {
      ...axiosConfig,
      headers: { ...(tokens ? { Authorization: `Bearer ${tokens.accessToken}` } : {}), ...axiosConfig.headers },
    });
  };
}

export const driverApi = new API(constants.driverApi);

export const getFreshTokens = async (): Promise<LoginOutput | null> => {
  const tokens = queryClient.getQueryData<LoginOutput>(queryKeys.tokens());

  if (!tokens) {
    return null;
  }

  if (dayjs(tokens.expires).diff(Date.now(), 'minutes') < 5) {
    const data = await driverApi.post<RefreshInput, RefreshOutput>(
      '/authentication/v1/token/refresh',
      { accessToken: tokens.accessToken, refreshToken: tokens.refreshToken },
      { skipAuth: true },
    );

    const refreshedTokens = {
      accessToken: data.accessToken,
      refreshToken: tokens.refreshToken,
      issued: data.issued,
      expires: data.expires,
    };

    updateTokens(queryClient, refreshedTokens);

    return refreshedTokens;
  }

  return tokens;
};
